import { initializeApp, getApp, getApps } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  enableMultiTabIndexedDbPersistence,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

if (!firebaseConfig.projectId) {
  console.warn(
    'Firebase environment variables are not configured. Copy .env.example to .env.local.'
  );
}

// Reuse the default app during React Fast Refresh/HMR instead of creating
// another Firebase app instance.
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Keep Firebase Auth persistence out of IndexedDB.
// This avoids "Database is closing/hidden" Auth persistence errors while
// allowing the application database to keep its own offline cache below.
let authInstance;

try {
  authInstance = initializeAuth(app, {
    persistence: [
      browserLocalPersistence,
      browserSessionPersistence,
      inMemoryPersistence,
    ],
  });
} catch (error) {
  if (error?.code === 'auth/already-initialized') {
    authInstance = getAuth(app);
  } else {
    throw error;
  }
}

export const auth = authInstance;

// IMPORTANT:
// "Database" is the user-facing name used inside CoachSphere.
// The Firebase Web SDK function itself is still named getFirestore().
// Do NOT rename Firebase SDK imports such as getFirestore,
// connectFirestoreEmulator, collection, doc, query, etc.
export const db = getFirestore(app);

// Keep application database offline persistence separate from Auth persistence.
const browserOfflineEnabled =
  typeof window === 'undefined'
    ? true
    : window.localStorage.getItem('coachsphere_offline_enabled') !== 'false';

if (
  typeof window !== 'undefined' &&
  browserOfflineEnabled &&
  process.env.REACT_APP_DISABLE_OFFLINE !== 'true'
) {
  enableMultiTabIndexedDbPersistence(db).catch((error) => {
    if (!['failed-precondition', 'unimplemented'].includes(error?.code)) {
      console.warn(
        'Database offline persistence unavailable; continuing online:',
        error?.message || error
      );
    }
  });
}

export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

let emulatorsConnected = false;

if (process.env.REACT_APP_USE_EMULATORS === 'true' && !emulatorsConnected) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
    disableWarnings: true,
  });

  // Firebase SDK API name must remain connectFirestoreEmulator().
  connectFirestoreEmulator(db, '127.0.0.1', 8080);

  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

  emulatorsConnected = true;
}

export const PORTAL_MODE = process.env.REACT_APP_PORTAL_MODE || 'all';
export const PINNED_CENTER_ID = process.env.REACT_APP_CENTER_ID || '';
